import React, { ChangeEvent, useEffect, useState } from 'react'
import * as actions from './actions'
import { useParams, Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { InitialState, FetchLessonType } from './types'
import DatePicker, { registerLocale } from 'react-datepicker'
import { useAlert } from 'react-alert'
import Modal from 'react-modal'
import ru from 'date-fns/locale/ru'
import { declOfNum } from '../../utils/helpers'

import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'

// Components
import Header from '../../components/Header'
import Footer from '../../components/Footer'

registerLocale('ru', ru)

const customModalStyles = {
  content: {
    width: 400,
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
}

const customModalRelationStyles = {
  content: {
    width: 600,
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
}

export default () => {
  const [title, setTitle] = useState<string>('')
  const [icon, setIcon] = useState<File>()
  const [theme, setTheme] = useState<string>('')
  const [selectedModuleId, setSelectedModuleId] = useState<string>('')
  const [indexNumber, setIndexNumber] = useState<number>(0)
  const [timeToStudy, setTimeToStudy] = useState<number>(0)
  const [isFirst, setIsFirst] = useState<boolean>(false)
  const [isLast, setIsLast] = useState<boolean>(false)
  const [isFree, setIsFree] = useState<boolean>(false)
  const [availableAt, setAvailableAt] = useState<Date>(new Date())
  const [assistantCaption, setAssistantCaption] = useState<string>('')
  const [position, setPosition] = useState<number>(0)
  const [editModalIsOpen, setEditModalIsOpen] = useState<boolean>(false)
  const [editConnectId, setEditConnectId] = useState<string>('')
  const [relationModal, setRelationModal] = useState<boolean | string>(false)

  const { id, lid } = useParams<FetchLessonType>()

  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    lesson,
    themes,
    modules,
    isLessonFetching,
    isThemesFetching,
    isModulesFetching,
    error,
  } = useSelector(
    (state: { adminLessonEdit: InitialState }) => state.adminLessonEdit
  )

  useEffect(() => {
    if (error) {
      alert.error(error)
    }
  }, [error])

  useEffect(() => {
    dispatch(actions.fetchLesson(id, lid))
    dispatch(actions.fetchThemes(id))
    dispatch(actions.fetchModules(id))
    return () => {
      dispatch(actions.destroy())
    }
  }, [])

  useEffect(() => {
    setTitle(lesson.title)
    setTheme(lesson.theme_id)
  }, [lesson])

  useEffect(() => {
    if (editConnectId) {
      lesson.module_lessons
        ?.filter(lesson => lesson.module_lesson_id === editConnectId)
        .forEach(item => {
          setIndexNumber(item.index_number)
          setAssistantCaption(item.assistant_caption)
          setTimeToStudy(item.time_to_study)
          setIsFirst(item.is_first)
          setIsLast(item.is_last)
          setIsFree(item.is_free)
          setAvailableAt(
            item.available_at ? moment(item.available_at).toDate() : new Date()
          )
          setPosition(item.position)
        })
    }
  }, [editConnectId])

  const clearModuleForm = () => {
    setAssistantCaption('')
    setIndexNumber(0)
    setEditModalIsOpen(false)
    setEditConnectId('')
    setTimeToStudy(0)
    setIsFirst(false)
    setIsLast(false)
    setIsFree(false)
    setAvailableAt(new Date())
    setPosition(0)
  }

  const onChangeTitle = (event: ChangeEvent<HTMLInputElement>) => {
    setTitle(event.currentTarget.value)
  }

  const onChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0]
      setIcon(file)
    }
  }

  const onChangeTheme = (event: ChangeEvent<HTMLSelectElement>) => {
    setTheme(event.currentTarget.value)
  }

  const onChangeModule = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedModuleId(event.currentTarget.value)
  }

  const onChangeIndexNumber = (event: ChangeEvent<HTMLInputElement>) => {
    setIndexNumber(parseInt(event.currentTarget.value))
  }

  const onChangeTimeToStudy = (event: ChangeEvent<HTMLInputElement>) => {
    setTimeToStudy(parseInt(event.currentTarget.value))
  }

  const onChangePosition = (event: ChangeEvent<HTMLInputElement>) => {
    setPosition(parseInt(event.currentTarget.value))
  }

  const onChangeAssistantCaption = (event: ChangeEvent<HTMLInputElement>) => {
    setAssistantCaption(event.target.value)
  }

  const onChangeIsFirst = (event: ChangeEvent<HTMLInputElement>) => {
    setIsFirst(event.target.checked)
  }

  const onChangeIsLast = (event: ChangeEvent<HTMLInputElement>) => {
    setIsLast(event.currentTarget.checked)
  }

  const onChangeIsFree = (event: ChangeEvent<HTMLInputElement>) => {
    setIsFree(event.currentTarget.checked)
  }

  const handleConnectModuleLesson = () => {
    dispatch(
      actions.connectModuleLesson(
        id,
        selectedModuleId,
        lid,
        indexNumber,
        assistantCaption,
        timeToStudy,
        isFirst,
        isLast,
        isFree,
        moment(availableAt),
        position
      )
    )
    setSelectedModuleId('')
  }

  const handleEditLesson = () => {
    dispatch(actions.editLesson(lid, id, title, icon, theme))
  }

  const handleModuleLessonRemove = (moduleLessonId: string) => {
    const isLast = lesson.module_lessons?.length === 1
    dispatch(actions.removeModuleLesson(id, lid, moduleLessonId, isLast))
  }

  const handleEditModuleLesson = () => {
    dispatch(
      actions.editModuleLesson(
        id,
        lid,
        indexNumber,
        assistantCaption,
        editConnectId,
        timeToStudy,
        isFirst,
        isLast,
        isFree,
        moment(availableAt),
        position
      )
    )
    clearModuleForm()
  }

  const handleCreateLessonRelation = (target: string) => {
    dispatch(
      actions.createLessonRelation(
        id,
        relationModal,
        'blocks',
        lesson.lesson_id,
        target
      )
    )
  }

  const handleEditLessonRelation = (lessonRelation: string, target: string) => {
    dispatch(
      actions.editLessonRelation(
        lessonRelation,
        id,
        relationModal,
        'blocks',
        lesson.lesson_id,
        target
      )
    )
  }

  return (
    <>
      <Header />
      <div className={'container'}>
        <h2>Панель администрирования - Редактирование урока</h2>
        {isLessonFetching ? (
          <span>Загрузка урока...</span>
        ) : (
          <>
            <label htmlFor={'title'}>Название урока:</label>
            <br />
            <input
              id={'title'}
              placeholder={'Введите название урока'}
              value={title}
              onChange={onChangeTitle}
              style={{ marginBottom: 10, width: 200 }}
              required={true}
              autoComplete={'off'}
            />
            <br />
            <span style={{ display: 'block', marginBottom: 10 }}>
              Выбранная иконка:
              <br />
              {icon ? (
                <b>{icon?.name}</b>
              ) : lesson?.icon_media?.url ? (
                <img src={lesson?.icon_media?.url} width={200} />
              ) : (
                <b>Иконка не выбрана</b>
              )}
            </span>
            <label
              htmlFor={'icon'}
              style={{
                cursor: 'pointer',
                border: '1px solid grey',
                borderRadius: 6,
                padding: '0 6px',
                marginBottom: 10,
                display: 'block',
                width: 200,
                textAlign: 'center',
              }}
            >
              Выбрать иконку
            </label>
            <input
              onChange={onChangeFile}
              id={'icon'}
              type={'file'}
              style={{ display: 'none' }}
            />
            <label htmlFor={'theme'}>Тема:</label>
            <br />
            {themes.length ? (
              <select
                value={theme}
                onChange={onChangeTheme}
                style={{ marginBottom: 10, width: 200 }}
              >
                <option value="" disabled>
                  Выберите тему
                </option>
                {themes.map(theme => (
                  <option value={theme.theme_id} key={theme.theme_id}>
                    {theme.title}
                  </option>
                ))}
              </select>
            ) : isThemesFetching ? (
              <>
                <span>Загрузка тем...</span>
                <br />
              </>
            ) : null}
            <br />
            <button onClick={handleEditLesson}>Сохранить</button>
            <br />
            <br />
            {isModulesFetching ? (
              <div style={{ marginTop: 15 }}>Загрузка модулей...</div>
            ) : (
              <>
                <h3>Модули</h3>
                {lesson.module_lessons ? (
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <td style={{ textAlign: 'center' }}>П/п</td>
                        <td>
                          <b>Модуль</b>
                        </td>
                        <td>
                          <b>Время на изучение</b>
                        </td>
                        <td>
                          <b>Первый урок</b>
                        </td>
                        <td>
                          <b>Последний урок</b>
                        </td>
                        <td>
                          <b>Свободный к прохождению</b>
                        </td>
                        <td>
                          <b>Время открытия урока</b>
                        </td>
                        <td />
                        <td />
                        <td />
                      </tr>
                    </thead>
                    <tbody>
                      {lesson.module_lessons.map(item => {
                        const relationsCount =
                          lesson.lesson_relations_blocks?.filter(
                            lessonRelation =>
                              lessonRelation.module_id === item.module_id
                          ).length || 0

                        return (
                          <tr key={item.module_lesson_id}>
                            <td style={{ width: 40, textAlign: 'center' }}>
                              {item.index_number}
                            </td>
                            <td style={{ width: 200 }}>
                              {
                                modules.find(
                                  module => module.module_id === item.module_id
                                )?.title
                              }
                              <br />
                              <a
                                style={{
                                  borderBottom: '1px dashed blue',
                                  cursor: 'pointer',
                                }}
                                onClick={() => setRelationModal(item.module_id)}
                              >
                                (
                                {relationsCount
                                  ? `${relationsCount} ${declOfNum(
                                      relationsCount,
                                      [
                                        'зависимость',
                                        'зависимости',
                                        'зависимостей',
                                      ]
                                    )} в модуле`
                                  : 'Нет зависимостей в модуле'}
                                )
                              </a>
                            </td>
                            <td>{item.time_to_study}</td>
                            <td>{item.is_first ? 'Да' : 'Нет'}</td>
                            <td>{item.is_last ? 'Да' : 'Нет'}</td>
                            <td>{item.is_free ? 'Да' : 'Нет'}</td>
                            <td>
                              {item.available_at
                                ? moment(item.available_at).format(
                                    'DD-MM-YYYY HH:mm:ss'
                                  )
                                : 'Не установлено'}
                            </td>
                            <td>
                              <Link
                                to={`/admin/courses/${id}/modules/${item.module_id}/lessons/${lid}/blocks`}
                              >
                                <button>Настроить содержимое</button>
                              </Link>
                            </td>
                            <td>
                              <button
                                onClick={() => {
                                  setSelectedModuleId('')
                                  setEditModalIsOpen(true)
                                  setEditConnectId(item.module_lesson_id)
                                }}
                              >
                                Изменить
                              </button>
                            </td>
                            <td>
                              <button
                                style={{ color: '#FFF', background: 'red' }}
                                onClick={() =>
                                  handleModuleLessonRemove(
                                    item.module_lesson_id
                                  )
                                }
                              >
                                X
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                ) : (
                  <></>
                )}
                <br />
                <span style={{ display: 'block', marginBottom: 5 }}>
                  Выберите модуль
                </span>
                <select
                  style={{ width: 200 }}
                  value={selectedModuleId}
                  onChange={onChangeModule}
                >
                  <option disabled={true} value={''}>
                    Выберите модуль
                  </option>
                  {modules.map(module => (
                    <option
                      disabled={module.lessons?.some(
                        lesson => lesson.lesson_id === lid
                      )}
                      key={module.module_id}
                      value={module.module_id}
                    >
                      {module.title}
                    </option>
                  ))}
                </select>
                <br />
                {selectedModuleId ? (
                  <div style={{ marginTop: 15 }}>
                    <label style={{ display: 'block', marginBottom: 5 }}>
                      Порядковый номер:
                    </label>
                    <input
                      type={'number'}
                      value={indexNumber}
                      style={{ width: 200, marginBottom: 10 }}
                      placeholder={'Укажите порядковый номер'}
                      onChange={onChangeIndexNumber}
                    />
                    <label style={{ display: 'block', marginBottom: 5 }}>
                      Время на изучение:
                    </label>
                    <input
                      type={'number'}
                      value={timeToStudy}
                      style={{ width: 200 }}
                      placeholder={'Укажите время на изучение'}
                      onChange={onChangeTimeToStudy}
                    />
                    <br />
                    <span
                      style={{
                        display: 'block',
                        marginBottom: 10,
                        fontSize: 12,
                      }}
                    >
                      Если не указано, будет использовано 90
                    </span>
                    <label
                      style={{ width: 200, display: 'block', marginBottom: 10 }}
                    >
                      Текст помощника
                      <input
                        value={assistantCaption}
                        type={'text'}
                        onChange={onChangeAssistantCaption}
                      />
                    </label>
                    <label
                      style={{ width: 200, display: 'block', marginBottom: 10 }}
                    >
                      Первый урок{' '}
                      <input
                        checked={isFirst}
                        type={'checkbox'}
                        onChange={onChangeIsFirst}
                      />
                    </label>
                    <label
                      style={{ width: 200, display: 'block', marginBottom: 10 }}
                    >
                      Последний урок{' '}
                      <input
                        checked={isLast}
                        type={'checkbox'}
                        onChange={onChangeIsLast}
                      />
                    </label>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      Свободный к прохождению{' '}
                      <input
                        checked={isFree}
                        type={'checkbox'}
                        onChange={onChangeIsFree}
                      />
                    </label>
                    <label style={{ display: 'block', marginBottom: 5 }}>
                      Время открытия урока
                    </label>
                    <DatePicker
                      locale={'ru'}
                      selected={availableAt}
                      onChange={(date: Date) => setAvailableAt(date)}
                      showTimeSelect={true}
                      dateFormat={'Pp'}
                    />
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 5,
                        marginTop: 5,
                      }}
                    >
                      Позиция:
                    </label>
                    <input
                      value={position}
                      type={'number'}
                      style={{ width: 200 }}
                      placeholder={'Укажите время на изучение'}
                      onChange={onChangePosition}
                    />
                    <br />
                    <button
                      onClick={handleConnectModuleLesson}
                      style={{ marginTop: 20, display: 'block' }}
                    >
                      Связать урок с модулем
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </>
        )}
        <Modal
          isOpen={editModalIsOpen}
          onRequestClose={clearModuleForm}
          style={customModalStyles}
          ariaHideApp={false}
        >
          <h3 style={{ margin: 0, marginBottom: 10 }}>
            Изменить связь с модулем
          </h3>
          <label style={{ display: 'block', marginBottom: 5 }}>
            Порядковый номер:
          </label>
          <input
            type={'number'}
            value={indexNumber}
            style={{ width: 200, marginBottom: 10 }}
            placeholder={'Укажите порядковый номер'}
            onChange={onChangeIndexNumber}
          />
          <label style={{ display: 'block', marginBottom: 5 }}>
            Время на изучение:
          </label>
          <input
            type={'number'}
            value={timeToStudy}
            style={{ width: 200 }}
            placeholder={'Укажите время на изучение'}
            onChange={onChangeTimeToStudy}
          />
          <br />
          <span style={{ display: 'block', marginBottom: 10, fontSize: 12 }}>
            Если не указано, будет использовано 90
          </span>
          <label style={{ width: 200, display: 'block', marginBottom: 10 }}>
            Текст помощника
            <input
              value={assistantCaption}
              type={'text'}
              onChange={onChangeAssistantCaption}
            />
          </label>
          <label style={{ width: 200, display: 'block', marginBottom: 10 }}>
            Первый урок{' '}
            <input
              checked={isFirst}
              type={'checkbox'}
              onChange={onChangeIsFirst}
            />
          </label>
          <label style={{ width: 200, display: 'block', marginBottom: 10 }}>
            Последний урок{' '}
            <input
              checked={isLast}
              type={'checkbox'}
              onChange={onChangeIsLast}
            />
          </label>
          <label style={{ display: 'block', marginBottom: 10 }}>
            Свободный к прохождению{' '}
            <input
              checked={isFree}
              type={'checkbox'}
              onChange={onChangeIsFree}
            />
          </label>
          <label style={{ display: 'block', marginBottom: 5 }}>
            Время открытия урока
          </label>
          <DatePicker
            locale={'ru'}
            selected={availableAt}
            onChange={(date: Date) => setAvailableAt(date)}
            showTimeSelect={true}
            dateFormat={'Pp'}
          />
          <label style={{ display: 'block', marginBottom: 5, marginTop: 5 }}>
            Позиция:
          </label>
          <input
            value={position}
            type={'number'}
            style={{ width: 200 }}
            placeholder={'Укажите время на изучение'}
            onChange={onChangePosition}
          />
          <br />
          <button
            onClick={handleEditModuleLesson}
            style={{ marginTop: 20, display: 'block' }}
          >
            Сохранить
          </button>
        </Modal>
        <Modal
          isOpen={!!relationModal}
          onRequestClose={() => setRelationModal(false)}
          style={customModalRelationStyles}
          ariaHideApp={false}
        >
          <h3 style={{ marginTop: 0 }}>Зависимости:</h3>
          <table>
            <thead>
              <tr>
                <td>
                  <b>Текущий урок</b>
                </td>
                <td>
                  <b>Блокирует</b>
                </td>
                <td>
                  <b>Целевой урок</b>
                </td>
                <td></td>
              </tr>
            </thead>
            <tbody>
              {lesson?.lesson_relations_blocks
                ?.filter(
                  lessonRelation => lessonRelation.module_id === relationModal
                )
                .map(lessonRelation => (
                  <tr key={lessonRelation.lesson_relation_id}>
                    <td>{lesson.title}</td>
                    <td>
                      <select value={'blocks'}>
                        <option value="blocks" disabled={true}>
                          Блокирует
                        </option>
                      </select>
                    </td>
                    <td style={{ width: 200 }}>
                      <select
                        style={{ width: 200 }}
                        onChange={event =>
                          handleEditLessonRelation(
                            lessonRelation.lesson_relation_id,
                            event.currentTarget.value
                          )
                        }
                      >
                        <option value={'add'} selected={true} disabled={true}>
                          Выберите целевой урок
                        </option>
                        {modules
                          .find(module => module.module_id === relationModal)
                          ?.lessons?.map(lessonItem => (
                            <option
                              value={lessonItem.lesson_id}
                              key={lessonItem.lesson_id}
                              disabled={
                                !!lesson?.lesson_relations_blocks?.find(
                                  lessonRelationAll =>
                                    lessonRelationAll.target_lesson_id ===
                                    lessonItem.lesson_id
                                ) || lessonItem.lesson_id === lesson.lesson_id
                              }
                              selected={
                                lessonItem.lesson_id ===
                                lessonRelation.target_lesson_id
                              }
                            >
                              {lessonItem.title}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td>
                      <button
                        style={{ backgroundColor: 'red', color: '#FFF' }}
                        onClick={() =>
                          dispatch(
                            actions.removeLessonRelation(
                              lessonRelation.lesson_relation_id,
                              id,
                              lesson.lesson_id
                            )
                          )
                        }
                      >
                        X
                      </button>
                    </td>
                  </tr>
                ))}
              <tr>
                <td>{lesson.title}</td>
                <td>
                  <select value={'blocks'}>
                    <option value="blocks" disabled={true}>
                      Блокирует
                    </option>
                  </select>
                </td>
                <td style={{ width: 200 }}>
                  <select
                    style={{ width: 200 }}
                    onChange={event =>
                      handleCreateLessonRelation(event.currentTarget.value)
                    }
                  >
                    <option value={'add'} selected={true} disabled={true}>
                      Выберите целевой урок
                    </option>
                    {modules
                      .find(module => module.module_id === relationModal)
                      ?.lessons?.map(lessonItem => (
                        <option
                          value={lessonItem.lesson_id}
                          key={lessonItem.lesson_id}
                          disabled={
                            !!lesson?.lesson_relations_blocks?.find(
                              lessonRel =>
                                lessonRel.target_lesson_id ===
                                lessonItem.lesson_id
                            ) || lessonItem.lesson_id === lesson.lesson_id
                          }
                        >
                          {lessonItem.title}
                        </option>
                      ))}
                  </select>
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </Modal>
        <br />
        {error ? <span style={{ color: 'red' }}>{error}</span> : null}
      </div>
      <Footer />
    </>
  )
}
